import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { Participant } from '@/types/participant.js';
import { useAuthStore, type User } from '@/stores/auth.ts';
import { toast } from '@pokerly/ui';
import type {
    UserJoinedCallback,
    UserLeftCallback,
    UserRoleUpdatedCallback,
    UserVotedCallback,
} from '@/services/pokerly-ws/callbacks.js';
import { useSessionStateStore } from '@/stores/session-state.ts';

/**
 * Participants store - manages session participants (users, voters, observers).
 */
export const useParticipantsStore = defineStore('participants', () => {
    const remoteParticipants = ref<Participant[]>([]);
    const userType = ref<string>('voter');

    // --- Event Handlers (to be registered with PokerlyWs) ---

    const onUserJoined: UserJoinedCallback = (payload) => {
        remoteParticipants.value = [...remoteParticipants.value.filter((p) => p?.id !== payload.user.id), payload.user];
        toast('User joined', {
            description: `${payload.user.name} has joined the session`,
            duration: 2000,
        });
    };

    const onUserLeft: UserLeftCallback = (payload) => {
        const user = remoteParticipants.value.find((p) => p.id === payload.id);
        if (!user) return;
        remoteParticipants.value = remoteParticipants.value.filter((participant) => {
            return participant.id !== payload.id;
        });
        toast('User left', {
            description: `${user.name} has left the session`,
            duration: 2000,
        });
    };

    const onUserVoted: UserVotedCallback = (payload) => {
        remoteParticipants.value = remoteParticipants.value.map((participant) => {
            if (participant.id !== payload.id) return participant;
            return { ...participant, vote: payload.vote };
        });
    };

    const onUserRoleUpdated: UserRoleUpdatedCallback = (payload) => {
        remoteParticipants.value = remoteParticipants.value.map((participant) => {
            if (participant.id !== payload.id) return participant;
            return { ...participant, role: payload.role };
        });
    };

    // --- Computed ---

    const thisUser = computed<Participant | undefined>(() => {
        const authStore = useAuthStore();
        return remoteParticipants.value.find((p) => p.id === authStore.user?.id);
    });

    const participants = computed<Participant[]>(() => {
        const authStore = useAuthStore();
        const currentUser = remoteParticipants.value.find((p) => p.id === authStore.user?.id);
        return [
            {
                ...(currentUser ?? {
                    ...authStore.user,
                    type: userType.value,
                }),
            },
            ...remoteParticipants.value.filter((p) => p && p.id !== authStore.user?.id),
        ] as Participant[];
    });

    const voters = computed(() => {
        return participants.value.filter((p) => p.role === 'voter' || !p.role);
    });

    const observers = computed(() => {
        return participants.value.filter((p) => p.role === 'observer');
    });

    // --- Actions ---

    function isAuthenticatedUser(participant: Participant | User): boolean {
        const authStore = useAuthStore();
        return participant.id === authStore.user?.id;
    }

    function setUserType(role: 'voter' | 'observer') {
        if (thisUser.value?.role === role) return;
        const authStore = useAuthStore();
        const userId = authStore.user?.id;
        remoteParticipants.value = remoteParticipants.value.map((participant) => {
            if (participant.id !== userId) return participant;
            return { ...participant, role };
        });
        const sessionState = useSessionStateStore();
        sessionState.getWs().setUserType(role);
    }

    function setParticipants(users: Participant[]) {
        remoteParticipants.value = users;
    }

    function clearVotes() {
        remoteParticipants.value = remoteParticipants.value.map((participant) => ({
            ...participant,
            vote: null,
        }));
    }

    function updateParticipantVote(userId: string, vote: number | string | null) {
        remoteParticipants.value = remoteParticipants.value.map((participant) => {
            if (participant.id !== userId) return participant;
            return { ...participant, vote };
        });
    }

    function $reset() {
        remoteParticipants.value = [];
        userType.value = 'voter';
    }

    return {
        // State
        remoteParticipants,
        userType,

        // Computed
        thisUser,
        participants,
        voters,
        observers,

        // Event handlers (for ws registration)
        onUserJoined,
        onUserLeft,
        onUserVoted,
        onUserRoleUpdated,

        // Actions
        isAuthenticatedUser,
        setUserType,
        setParticipants,
        clearVotes,
        updateParticipantVote,

        // Reset
        $reset,
    };
});
